import { SxProps } from '@mui/material';
import Link, { LinkProps as MUILinkProps } from '@mui/material/Link';
import {
  Link as ReactRouterLink,
  LinkProps as ReactRouterLinkProps,
} from 'react-router-dom';

import colors from '../../../constants/colors';

const colorMap = {
  color: {
    white: colors.csaaBlack,
    blue: colors.csaaWhite,
  },
  backgroundColor: {
    white: 'none',
    blue: colors.csaaSpectrumBlue50,
  },
  focusColor: {
    white: 'currentColor',
    blue: colors.csaaInteractiveBlue,
  },
  focusBackgroundColor: {
    white: colors.csaaOverlayBlack,
    blue: colors.csaaInteractiveBlue,
  },
  hoverBackgroundColor: {
    white: colors.blackInteractiveButtonHover,
    blue: colors.blueInteractiveButtonHover,
  },
};

const containedLinkStyles = (variantColor: 'blue' | 'white') => ({
  fontSize: {
    mobile: 16,
    tablet: 18,
  },
  lineHeight: '24px',
  fontWeight: 700,
  color: colorMap.color[variantColor],
  backgroundColor: colorMap.backgroundColor[variantColor],
  borderRadius: 1,
  border: variantColor === 'white' ? '2px solid currentColor' : 'none',
  height: '42px',
  width: { mobile: 1, tablet: 'auto' },
  minWidth: 'auto',
  px: 4,
  py: 1,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  '&:focus': {
    color: colorMap.color[variantColor],
    border: `1px solid ${colorMap.focusColor[variantColor]}`,
    outlineOffset: '1px',
    outline: `2px solid ${colorMap.focusColor[variantColor]}`,
    backgroundColor: colorMap.focusBackgroundColor[variantColor],
  },
  '&:hover': {
    backgroundColor: colorMap.hoverBackgroundColor[variantColor],
    color: colorMap.color[variantColor],
    textDecoration: 'none',
  },
});

interface StyledLinkType extends MUILinkProps {
  variantColor: 'white' | 'blue';
}

const StyledLink: React.FC<ReactRouterLinkProps & MUILinkProps> = ({
  children,
  ...rest
}) => {
  return (
    <Link
      component={ReactRouterLink}
      underline="none"
      sx={{
        fontSize: {
          mobile: 16,
          tablet: 18,
        },
        fontWeight: 400,
        fontFamily: 'Cabin',
        fontKerning: 'normal',
        color: colors.csaaSpectrumBlue50,
        ...rest.sx,
      }}
      {...rest}
    >
      {children}
    </Link>
  );
};

export default StyledLink;

export const LinkWithNoRouter: React.FC<MUILinkProps> = ({
  children,
  href,
  ...rest
}) => (
  <Link
    data-testid="styledLink"
    href={href}
    underline="none"
    sx={{
      color: 'primary',
      fontWeight: { mobile: 400, tablet: 700 },
      fontSize: { mobile: 13, tablet: 18 },
      ...rest.sx,
    }}
    {...rest}
  >
    {children}
  </Link>
);

export const ContainedLink: React.FC<ReactRouterLinkProps & StyledLinkType> = ({
  children,
  variantColor = 'white',
  ...rest
}) => {
  return (
    <Link
      {...rest}
      component={ReactRouterLink}
      underline="none"
      sx={
        {
          ...rest.sx,
          ...containedLinkStyles(variantColor),
        } as SxProps
      }
    >
      {children}
    </Link>
  );
};

export const ContainedExternalLink: React.FC<StyledLinkType> = ({
  children,
  variantColor = 'white',
  ...rest
}) => {
  return (
    <Link
      {...rest}
      underline="none"
      sx={
        {
          ...rest.sx,
          ...containedLinkStyles(variantColor),
        } as SxProps
      }
    >
      {children}
    </Link>
  );
};
